import { useEffect, useMemo } from 'react';

import useToggle from '@lyearn/core/hooks/useToggle';
import Platform from '@lyearn/core/utils/Platform';
import { useUserContext } from '@/modules/auth/hooks/useUser';
import { UserSubscriptionOperationEnum } from '@/types/schema';
import { getCurrentTimeZoneValue } from '@/utils/timezone';

import { useUpdateLastResolvedNotificationMutation } from '../graphql/mutations/generated/updateLastResolvedNotification';
import { useGetNotificationCountQuery } from '../graphql/queries/generated/getNotificationCount';
import { useUserBellNotificationSubscription } from '../graphql/subscription/generated/userBellNotification';

const isNative = Platform.OS !== 'web';

function useNotificationCount() {
  const [open, { toggle }] = useToggle(false);
  const user = useUserContext();
  const [data] = useGetNotificationCountQuery({
    variables: {
      filters: {
        userId: user.id,
      },
    },
  });

  useUserBellNotificationSubscription({
    variables: {
      input: {
        operations: [UserSubscriptionOperationEnum.UserBellNotification],
        userTimezone: getCurrentTimeZoneValue(),
      },
    },
  });

  const { unresolvedNotificationCount, unreadNotificationsCount } = useMemo(() => {
    const user = data.data?.user;

    return {
      unresolvedNotificationCount: user?.unresolvedNotificationsCount,
      unreadNotificationsCount: user?.unreadNotificationsCount,
    };
  }, [data.data?.user]);

  const [, updateLastResolvedNotificationMutation] = useUpdateLastResolvedNotificationMutation();

  const onClick = () => {
    !isNative && toggle();
    if (!open && unresolvedNotificationCount) {
      updateLastResolvedNotificationMutation({});
    }
  };

  useEffect(() => {
    if (open && unresolvedNotificationCount) {
      updateLastResolvedNotificationMutation({});
    }
  }, [open, unresolvedNotificationCount, updateLastResolvedNotificationMutation]);

  return {
    open,
    onClick,
    unresolvedNotificationCount,
    unreadNotificationsCount,
    toggle,
  };
}

export default useNotificationCount;
